import {useCallback, useEffect, useState} from 'react';
import {useSwipeable} from 'react-swipeable';
import {useSetCarouselInterval} from './setCarouselInterval';

interface IUseCarouselNavigation {
  itemCount: number;
  wrap: boolean;
  autoplayIntervalMs?: number;
  externallyPaused?: boolean;
}

export const useCarouselNavigation = ({
  itemCount,
  wrap,
  autoplayIntervalMs = 0,
  externallyPaused = false,
}: IUseCarouselNavigation) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isSlideshowPaused, setIsSlideshowPaused] = useState(false);

  useEffect(() => {
    if (!externallyPaused) {
      setIsSlideshowPaused(false);
    }
  }, [externallyPaused]);

  const onGoToPreviousSlide = useCallback(() => {
    setIsSlideshowPaused(true);
    setCurrentSlideIndex(value => {
      if (value === 0) {
        return wrap ? itemCount - 1 : 0;
      }
      return value - 1;
    });
  }, [itemCount, wrap]);

  const onGoToNextSlide = useCallback(() => {
    setIsSlideshowPaused(true);
    setCurrentSlideIndex(value => {
      if (value === itemCount - 1) {
        return wrap ? 0 : value;
      }
      return value + 1;
    });
  }, [itemCount, wrap]);

  useSetCarouselInterval({
    isSlideshowPaused:
      autoplayIntervalMs === 0 ? true : isSlideshowPaused || externallyPaused,
    currentSlideIndex,
    lastSlideIndex: itemCount,
    onSetCurrentSlideIndex: setCurrentSlideIndex,
    time: autoplayIntervalMs || 2900,
    wrap,
  });

  const swipeHandlers = useSwipeable({
    onSwipedLeft: onGoToNextSlide,
    onSwipedRight: onGoToPreviousSlide,
    trackMouse: true,
    preventScrollOnSwipe: true,
    onTap: () => {
      setIsSlideshowPaused(true);
    },
  });

  return {
    currentSlideIndex,
    setCurrentSlideIndex,
    onGoToPreviousSlide,
    onGoToNextSlide,
    isAtStart: currentSlideIndex === 0,
    isAtEnd: currentSlideIndex === itemCount - 1,
    swipeHandlers,
  };
};
